import type { DestroyOptions, Sprite } from 'pixi.js';
import { Container, Texture } from 'pixi.js';
import type { BoosterButtonLayoutState } from './types';
import { BoosterButtonLayout } from '../../../../layouts/boosters/BoosterButtonLayout';
import type { BoosterDefinition } from '../types';
import { BoosterType } from '../types';
import { applyLayoutState, revokeLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import { BoosterButtonBottomLayout } from '../../../../layouts/boosters/BoosterButtonBottomLayout';
import type { AppStateController } from '../../../app/state/AppStateController';
import { addClickReaction } from '../../utils/addClickReaction';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';

export class BoosterButtonView extends Container {
  private readonly buttonLayout = new BoosterButtonLayout();

  private readonly bottomLayout: BoosterButtonBottomLayout = new BoosterButtonBottomLayout();

  #enabled = true;

  #attractMode = false;

  constructor(
    private readonly state: AppStateController,
    public readonly boosterType: BoosterType,
    private readonly economyModel: BoosterDefinition,
    onClick: (button: BoosterButtonView) => void,
    private readonly canShowShop: () => boolean,
  ) {
    super();

    const { buttonLayout, bottomLayout } = this;

    this.addChild(buttonLayout.root, bottomLayout.root);

    this.eventMode = 'static';
    this.cursor = 'pointer';

    bottomLayout.root.props({
      y: buttonLayout.buttonGroup.height / 2,
    });

    buttonLayout.attractOverlay.props({
      alpha: 0,
    });

    this.onpointertap = (): void => {
      onClick(this);
    };

    addClickReaction({ target: this });

    this.updatePurchasableState();

    this.setInventoryAmount(state.getBoosterInventoryAmount(this.boosterType));
    this.addStateSubscriptions();

    applyLayoutState(buttonLayout, this.getButtonLayoutState());

    if (this.isLocked()) this.enabled = false;
  }

  public destroy(options?: DestroyOptions): void {
    this.removeStateSubscriptions();
    super.destroy(options);
  }

  public getIconSprite(): Sprite {
    return this.buttonLayout.icon;
  }

  private getButtonLayoutState(): BoosterButtonLayoutState {
    const boosterName = BoosterType[this.boosterType].toString().toLowerCase();
    return `${boosterName}-${this.#enabled ? 'enabled' : 'disabled'}` as BoosterButtonLayoutState;
  }

  set enabled(value: boolean) {
    if (this.#enabled === value || (value && this.isLocked())) return;

    const prevLayoutState = this.getButtonLayoutState();
    this.#enabled = value;

    const newLayoutState = this.getButtonLayoutState();
    const { buttonLayout } = this;

    this.interactive = this.#enabled;

    revokeLayoutState(buttonLayout, prevLayoutState);
    applyLayoutState(buttonLayout, newLayoutState);
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  public setInventoryAmount(amount: number): void {
    if (this.isLocked()) return;

    const { indicatorText, indicator } = this.buttonLayout;
    const isIAPEnabled = this.canShowShop();
    if (amount === 0) {
      indicator.texture = Texture.from('Plus_Button');
      indicator.scale.set(0.8);
      indicatorText.text = '';
      indicator.visible = isIAPEnabled;
      this.bottomLayout.root.visible = true;
      this.bottomLayout.coinAmountText.text = this.economyModel.coinCost;
      applyLayoutState(this.bottomLayout, 'coin-purchase');
    } else {
      indicator.visible = true;
      indicator.texture = Texture.from('Booster_Inventory_Background');
      indicator.scale.set(1);
      indicatorText.text = amount.toString();
      this.bottomLayout.root.visible = false;
    }
  }

  private isLocked(): boolean {
    const { state, economyModel } = this;
    return state.getLevelNumber() < economyModel.unlockLevel;
  }

  private updatePurchasableState(): void {
    const { buttonLayout, bottomLayout } = this;
    const { unlockLevel } = this.economyModel;

    if (this.isLocked()) {
      applyLayoutState(bottomLayout, 'locked');
      bottomLayout.lockedLevelText.text = `${getLocalizedString('Lvl')} ${unlockLevel}`;
      buttonLayout.indicator.visible = false;
    } else {
      bottomLayout.root.visible = false;
    }
  }

  public set attractMode(value: boolean) {
    if (this.#attractMode === value) return;
    this.#attractMode = value;

    const {
      buttonLayout: { attractOverlay },
    } = this;

    attractOverlay.removeTweens();

    if (this.#attractMode) {
      attractOverlay.tweenTo({ alpha: 1 }, 500, 'quadInOut', true, 1500, -1, true);
    } else {
      attractOverlay.tweenTo({ alpha: 0 }, 250, 'quadOut', true);
    }
  }

  public get attractMode(): boolean {
    return this.#attractMode;
  }

  private addStateSubscriptions(): void {
    const { state } = this;
    state.subscriptions.onChange(
      ['boosters'],
      () => {
        const inventoryAmount = state.getBoosterInventoryAmount(this.boosterType);
        this.setInventoryAmount(inventoryAmount);
      },
      this,
    );
  }

  private removeStateSubscriptions(): void {
    const { state } = this;
    state.subscriptions.removeAllSubscriptions(this);
  }
}
